/**
 * @fileoverview Main application configuration file that sets up core Angular providers,
 * authentication (MSAL), internationalization, and third-party integrations.
 *
 * Updated for Angular 2026 best practices:
 * - Uses ApplicationConfig type from @angular/core
 * - Uses withEventReplay() for improved SSR hydration
 * - Uses withComponentInputBinding() for route params as inputs
 */

// MSAL
import {
  MsalService,
  MsalGuard,
  MSAL_INTERCEPTOR_CONFIG,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';

// Angular Core
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';

// Third-party Libraries
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';

// Application Specific
import { routes } from './app.routes';
import { LuminavLinkClientModule } from './luminavlink-client.module';
import { StaticTranslationLoader } from './static/static-translations.loader';
import {
  msalConfig,
  msalGuardConfig,
  msalInterceptorConfig,
} from './auth-config';
import { loginRequest } from 'src/configurations/b2c/b2c.config';
import { HttpHandlerInterceptor } from './interceptors/http-handler.interceptor';
import { API_BASE_URL } from 'src/httpClient/luminav-link-api';
import { environment } from 'src/environments/environment';

// Create MSAL Instance
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL before the app starts.
 *
 * After `initialize()`, `handleRedirectPromise()` MUST run so the auth
 * code/hash returned by Azure B2C is processed before the router and
 * MsalGuard activate. Skipping this causes a blank page on first SSO
 * login (the guard starts a second interactive login over the pending
 * redirect and fails with `server_error` / `interaction_in_progress`).
 *
 * `handleRedirectPromise()` REJECTS when B2C returns an authentication
 * error (e.g. `AADB2C99002: User does not exist` for an account that was
 * deleted from B2C but still has a record in the application database).
 * An unhandled rejection in an APP_INITIALIZER aborts Angular bootstrap,
 * leaving the user on a blank/white screen. Catching it and re-initiating
 * the sign-in redirect sends the user back to the B2C login page — the
 * same recovery a user with no account at all gets via the MsalGuard.
 */
function initializeMsal() {
  return async () => {
    await msalInstance.initialize();
    try {
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      }
    } catch (error) {
      // B2C returns `AADB2C99002: User does not exist` when the account was
      // removed from B2C but still has a record in the application database.
      // The rejection would otherwise abort bootstrap and leave a blank
      // screen, so send the user back to the login page instead.
      if (isUserDoesNotExistError(error)) {
        console.warn('B2C user does not exist; redirecting to login.', error);
      } else {
        // Any other failed auth redirect must not abort bootstrap either;
        // returning to login is the safe recovery.
        console.error('MSAL redirect handling failed; redirecting to login.', error);
      }
      try {
        await msalInstance.loginRedirect(loginRequest);
      } catch (redirectError) {
        console.error('Failed to redirect to login.', redirectError);
      }
    }
  };
}

/**
 * Detects the Azure AD B2C "user does not exist" error (`AADB2C99002`),
 * raised when a user was deleted from B2C but still has an application
 * database record.
 *
 * @param error - The error thrown by `handleRedirectPromise()`
 * @returns `true` if the error is the B2C `AADB2C99002` error
 */
function isUserDoesNotExistError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : String(error ?? '');
  return message.includes('AADB2C99002');
}

/**
 * Initializes FontAwesome icon library with configured icon packs.
 * @param library - The FontAwesome library instance to initialize
 * @throws {Error} If icon pack initialization fails
 */
function initializeFaIcons(library: FaIconLibrary): void {
  try {
    library.addIconPacks(far, fas, fal);
  } catch (error) {
    console.error('Failed to initialize FontAwesome icons:', error);
    throw error;
  }
}

/**
 * Main application configuration object that sets up all necessary providers
 * and configurations for the Angular application.
 *
 * This configuration includes:
 * - Authentication (MSAL)
 * - Routing
 * - HTTP Client
 * - Animations
 * - Internationalization
 * - Toast Notifications
 * - Font Awesome Icons
 * - Custom Client Module
 *
 * @type {AppConfig}
 */
export const appConfig: ApplicationConfig = {
  providers: [
    // Routing Configuration with component input binding for route params
    provideRouter(routes, withComponentInputBinding()),

    // Core Application Providers
    { provide: API_BASE_URL, useValue: environment.base_url },

    // Zone.js Configuration for Change Detection
    provideZoneChangeDetection({
      eventCoalescing: true,
      runCoalescing: true,
    }),

    // Client-side Hydration with event replay for better SSR experience
    provideClientHydration(withEventReplay()),

    // HTTP Client with Interceptors and Fetch API for better performance
    provideHttpClient(withInterceptors([HttpHandlerInterceptor]), withFetch()),

    // Animations Support
    provideAnimationsAsync(),

    // Font Awesome Icon Library
    {
      provide: FaIconLibrary,
      useFactory: () => {
        const library = new FaIconLibrary();
        initializeFaIcons(library);
        return library;
      },
    },

    // MSAL Configuration
    {
      provide: MSAL_INSTANCE,
      useValue: msalInstance,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: msalGuardConfig,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: msalInterceptorConfig,
    },

    // Initialize MSAL before app starts
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      multi: true,
    },

    // MSAL Services
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Third-party Module Providers
    importProvidersFrom([
      // Internationalization Module
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: StaticTranslationLoader,
        },
      }),

      // Toast Notifications Module
      ToastrModule.forRoot({
        timeOut: 4000,
        positionClass: 'toast-top-right',
        preventDuplicates: true,
        progressBar: true,
        closeButton: true,
      }),

      // Custom Client Module
      LuminavLinkClientModule.forRoot(),
    ]),
  ],
};
